import { z } from "zod";

import { jerboaBrowserInfoSchema } from "~/modules/jerboa/schemas";

export const jerboaClientExportStatusSchema = z.enum(["finish", "error", "cancel"]);
export type JerboaClientExportStatus = z.infer<typeof jerboaClientExportStatusSchema>;

const jerboaClientExportEditTypeSchema = z.enum(["captions", "ai-edit"]);

const jerboaClientExportResolutionSchema = z.enum(["hd", "4k"]);

const jerboaClientExportLifecycleSpanNameSchema = z.enum([
  "worker_spawn",
  "wasm_load",
  "wasm_init",
  "webgpu_init",
  "video_fetch",
  "first_frame",
  "project_load",
  "encoder_configure",
  "encoder_warmup",
  "render_loop",
  "encoder_flush",
  "mux",
  "blob_assemble",
]);
export type JerboaClientExportLifecycleSpanName = z.infer<
  typeof jerboaClientExportLifecycleSpanNameSchema
>;

const jerboaClientExportLifecycleSpanStatusSchema = z.enum(["ok", "failed", "skipped"]);
export type JerboaClientExportLifecycleSpanStatus = z.infer<
  typeof jerboaClientExportLifecycleSpanStatusSchema
>;

const jerboaClientExportErrorCodeSchema = z.enum([
  "worker_spawn_failed",
  "wasm_compile_failed",
  "wasm_runtime_panic",
  "webgpu_not_available",
  "webgpu_device_lost",
  "segmentation_model_load_failed",
  "segmentation_inference_failed",
  "project_load_failed",
  "codec_not_supported",
  "encoder_error",
  "oom",
  "user_aborted",
  "tab_hidden_timeout",
  "unknown",
]);
export type JerboaClientExportErrorCode = z.infer<typeof jerboaClientExportErrorCodeSchema>;

export const jerboaExportMethodSchema = z.enum(["client", "server"]);
export type JerboaExportMethod = z.infer<typeof jerboaExportMethodSchema>;

export const jerboaServerExportStatusSchema = z.enum(["COMPLETE", "FAILED", "CANCELED"]);
export type JerboaServerExportStatus = z.infer<typeof jerboaServerExportStatusSchema>;

export const jerboaClientFailurePhaseSchema = z.enum(["wasm_load", "wasm_init", "export"]);
export type JerboaClientFailurePhase = z.infer<typeof jerboaClientFailurePhaseSchema>;

const jerboaClientExportLifecycleSpanSchema = z.object({
  name: jerboaClientExportLifecycleSpanNameSchema,
  startedAtMs: z.number(),
  durationMs: z.number(),
  status: jerboaClientExportLifecycleSpanStatusSchema,
  errorCode: jerboaClientExportErrorCodeSchema.nullish(),
  errorMessage: z.string().nullish(),
});
export type JerboaClientExportLifecycleSpan = z.infer<typeof jerboaClientExportLifecycleSpanSchema>;

const jerboaClientExportEventSchema = z.object({
  source: z.string(),
  name: z.string(),
  timestamp: z.string(),
  properties: z.record(z.string(), z.unknown()),
});

const jerboaClientExportSummarySchema = z.object({
  id: z.string(),
  jobId: z.string(),
  downloadAttemptId: z.string().nullish(),
  videoId: z.string(),
  projectId: z.string(),
  captionsProjectId: z.string().nullish(),
  status: jerboaClientExportStatusSchema,
  exportMethod: jerboaExportMethodSchema.nullish(),
  operationId: z.string().nullish(),
  serverExportStatus: jerboaServerExportStatusSchema.nullish(),
  editType: jerboaClientExportEditTypeSchema.nullish(),
  resolution: jerboaClientExportResolutionSchema,
  fps: z.number(),
  width: z.number(),
  height: z.number(),
  durationMs: z.number().nullish(),
  outputSizeBytes: z.number().nullish(),
  errorCategory: z.string().nullish(),
  errorCode: jerboaClientExportErrorCodeSchema.nullish(),
  avgPlaybackFps: z.number().nullish(),
  p90PlaybackFps: z.number().nullish(),
  avgFrameRenderMs: z.number().nullish(),
  p90FrameRenderMs: z.number().nullish(),
  targetFps: z.number().nullish(),
  appVersion: z.string().nullish(),
  createdAt: z.string(),
  userId: z.string().nullish(),
  orgId: z.string().nullish(),
});

const jerboaClientExportDetailSchema = jerboaClientExportSummarySchema.extend({
  totalFrames: z.number().nullish(),
  codec: z.string().nullish(),
  errorMessage: z.string().nullish(),
  serverExportError: z.string().nullish(),
  clientFailureErrorCode: jerboaClientExportErrorCodeSchema.nullish(),
  clientFailureErrorMessage: z.string().nullish(),
  clientFailureErrorCategory: z.string().nullish(),
  clientFailurePhase: jerboaClientFailurePhaseSchema.nullish(),
  protoDataUrl: z.string().nullish(),
  assetUrlMap: z.record(z.string(), z.string()).nullish(),
  concurrentExportCount: z.number().nullish(),
  autoDownload: z.boolean().nullish(),
  wasmLoadDurationMs: z.number().nullish(),
  wasmInitDurationMs: z.number().nullish(),
  framesRendered: z.number().nullish(),
  p50PlaybackFps: z.number().nullish(),
  p99PlaybackFps: z.number().nullish(),
  p50FrameRenderMs: z.number().nullish(),
  p99FrameRenderMs: z.number().nullish(),
  maxFrameRenderMs: z.number().nullish(),
  longestFrameGapMs: z.number().nullish(),
  slowFrameCount: z.number().nullish(),
  decoderErrorCount: z.number().nullish(),
  stalledOnDecodeCount: z.number().nullish(),
  fpsBuckets: z.array(z.number()).nullish(),
  bucketWindowMs: z.number().nullish(),
  peakWasmMemoryMb: z.number().nullish(),
  avgWasmMemoryMb: z.number().nullish(),
  peakWasmLiveMb: z.number().nullish(),
  avgWasmLiveMb: z.number().nullish(),
  peakJsHeapMb: z.number().nullish(),
  avgJsHeapMb: z.number().nullish(),
  wasmMemoryBuckets: z.array(z.number()).nullish(),
  wasmLiveBuckets: z.array(z.number()).nullish(),
  jsHeapBuckets: z.array(z.number()).nullish(),
  lifecycleSpans: z.array(jerboaClientExportLifecycleSpanSchema).nullish(),
  progressTMs: z.array(z.number()).nullish(),
  progressFrameIndices: z.array(z.number()).nullish(),
  progressEncoderQueueDepths: z.array(z.number()).nullish(),
  browserInfo: jerboaBrowserInfoSchema.nullish(),
  events: z.array(jerboaClientExportEventSchema),
});

export const jerboaClientExportsListResponseSchema = z.object({
  page: z.array(jerboaClientExportSummarySchema),
  pageInfo: z.object({
    hasNextPage: z.boolean(),
    endCursor: z.string().nullable(),
  }),
});

export const jerboaClientExportDetailResponseSchema = z.object({
  result: jerboaClientExportDetailSchema,
});

const clientExportRollingAggregateSchema = z.object({
  totalCount: z.number(),
  scoredCount: z.number(),
  successCount: z.number().default(0),
  errorCount: z.number(),
  cancelCount: z.number(),
  successRate: z.number().default(0),
  errorRate: z.number(),
  cancelRate: z.number(),
  speedMean: z.number(),
  speedMedian: z.number(),
  // Nullish, not a plain number: camel only asks the aggregate source for the
  // percentiles it can serve, so a window that measured no pc10 omits it. A
  // zero here would read as a measured export speed of 0x.
  speedP10: z.number().nullish(),
  speedP90: z.number(),
  // The export RUM action carries no playback-fps attributes, so camel serves
  // these as null rather than a fabricated 0 fps.
  avgFpsMean: z.number().nullish(),
  avgFpsMedian: z.number().nullish(),
  avgFpsP10: z.number().nullish(),
  avgFpsP90: z.number().nullish(),
  p90FpsMedian: z.number().nullish(),
});

const clientExportAnalysisBucketSchema = z.object({
  bucketStart: z.number(),
  bucketEnd: z.number(),
  totalCount: z.number(),
  scoredCount: z.number(),
  successCount: z.number(),
  errorCount: z.number(),
  cancelCount: z.number(),
  successRate: z.number().default(0),
  errorRate: z.number(),
  cancelRate: z.number(),
  speedMean: z.number(),
  speedMedian: z.number(),
  // See the rolling schema above: absent — not zero — when the aggregate
  // source served no pc10/pc25 for the window.
  speedP10: z.number().nullish(),
  speedP25: z.number().nullish(),
  speedP75: z.number(),
  speedP90: z.number(),
  // Nullish: the export RUM action carries no frame-render, playback-fps or
  // memory attributes, so camel has no request behind these and serves null.
  // A defaulted 0 would plot as a measured 0 fps / 0 ms / 0 MB.
  avgFrameRenderMsMedian: z.number().nullish(),
  p90FrameRenderMsMedian: z.number().nullish(),
  slowFrameRatioMedian: z.number().nullish(),
  avgFpsMean: z.number().nullish(),
  avgFpsMedian: z.number().nullish(),
  avgFpsP10: z.number().nullish(),
  avgFpsP25: z.number().nullish(),
  avgFpsP75: z.number().nullish(),
  avgFpsP90: z.number().nullish(),
  p90FpsMedian: z.number().nullish(),
  wasmLoadDurationMsMedian: z.number(),
  wasmInitDurationMsMedian: z.number(),
  peakWasmLiveMbP90: z.number().nullish(),
  peakJsHeapMbP90: z.number().nullish(),
  hdCount: z.number(),
  hdSpeedMedian: z.number(),
  k4Count: z.number(),
  k4SpeedMedian: z.number(),
  captionsCount: z.number(),
  aiEditCount: z.number(),
  errorCodeCounts: z.record(z.string(), z.number()).nullish(),
  appVersions: z.array(z.string()).nullish(),
  appVersionCounts: z.record(z.string(), z.number()).nullish(),
  appPrimaryVersion: z.string().nullish(),
  appPrimaryVersionShare: z.number().nullish(),
  appVersionCoverage: z.number().nullish(),
  appVersionFirstSeenInBucket: z.record(z.string(), z.number()).nullish(),
  releaseMarkers: z.array(z.object({ version: z.string(), firstSeenAt: z.number() })).nullish(),
  appVersionFirstSeenMap: z.record(z.string(), z.number()).nullish(),
  rolling1d: clientExportRollingAggregateSchema.nullish(),
  rolling7d: clientExportRollingAggregateSchema.nullish(),
});

export type ClientExportAnalysisBucket = z.infer<typeof clientExportAnalysisBucketSchema>;

export const clientExportAnalysisResponseSchema = z.object({
  analysis: z.object({
    buckets: z.array(clientExportAnalysisBucketSchema),
  }),
});
